import React, { useEffect } from 'react'
import { Card } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { get } from 'lodash'
import { Link } from 'react-router-dom'
import Lottie from 'lottie-react'
import classnames from 'classnames'

import styles from './styles.less'
import successAnimation from 'containers/onboarding/steps/animations/success-animation.json'
import loadingAnimation from 'containers/onboarding/steps/animations/loading-animation.json'
import errorAnimation from 'containers/onboarding/steps/animations/error-animation.json'

import { GET_PAYMENT_METHODS_V2 } from 'actions/billing-v2'
import { GET_PAYMENT_METHODS } from 'actions/billing.actions'
import { API } from 'actions/api'
import { useAllResourcesQuery, useAlarmsRulesQuery, useOrganizationUsersQuery, useNotificationChannelsQuery, useNotificationPoliciesQuery } from 'hooks/api'
import { useAccountRoutes } from 'containers/routes'
import { useOrganization } from 'hooks/context/organization-context'

function OnboardingChecklist ({ className }) {
  const dispatch = useDispatch()
  const routes = useAccountRoutes()

  const { organization, account } = useOrganization()
  const isUsingBillingV2 = organization?.metadata?.billing === 'v2'

  const { data: users, isLoading: usersLoading } = useOrganizationUsersQuery()

  const { data: resources, isLoading: resourcesLoading } = useAllResourcesQuery()
  const { data: alarmsRules, isLoading: alarmsLoading } = useAlarmsRulesQuery()
  const { data: channels, isLoading: channelsLoading } = useNotificationChannelsQuery()

  const { data: notificationPolicies, isLoading: notificationPoliciesLoading } = useNotificationPoliciesQuery()

  const paymentMethods = isUsingBillingV2 ? useSelector(state => state.billingV2.paymentMethods)?.data : useSelector(state => state.billing.payment.methods)
  const paymentMethodsLoading = useSelector(state => get(state.waiting.list, isUsingBillingV2 ? GET_PAYMENT_METHODS_V2 : GET_PAYMENT_METHODS))

  const checklist = [{
    loading: () => resourcesLoading,
    checked: () => (resources ? Object.keys(resources) : []).length > 0,
    text: 'Configure resources',
    url: routes.settings.integrations.url()
  }, {
    // can also use !!channels.find(channel => channel.email !== currentUser.email) if we want unique channel check
    loading: () => channelsLoading,
    checked: () => (channels || []).length > 1,
    text: 'Set up additional notification channels',
    url: routes.settings.notifications.url()
  }, {
    loading: () => alarmsLoading,
    checked: () => (alarmsRules || []).length > 0,
    text: 'Set up alarms',
    url: routes.alarms.url()
  }, {
    loading: () => notificationPoliciesLoading,
    checked: () => (notificationPolicies || []).length > 1,
    text: 'Set up additional event notifications',
    url: routes.settings.notifications.url()
  }, {
    loading: () => usersLoading,
    checked: () => (users || []).length > 1,
    text: 'Invite team members',
    url: routes.settings.users.url()
  }, {
    fetch: isUsingBillingV2 ? API.organizations.id(organization?.id).billingV2.paymentMethods.get : API.organizations.id(organization?.id).billing.payment.methods.get,
    loading: () => paymentMethodsLoading,
    checked: () => (paymentMethods || []).length > 0,
    text: 'Set up payment methods',
    url: routes.settings.payment.url()
  }]

  const lottie = (loading = true, success) => {
    let animationData = loadingAnimation
    if (!loading) {
      animationData = success ? successAnimation : errorAnimation
    }

    return (
      <Lottie
        animationData={animationData}
        style={{ flexShrink: 0, margin: '0 9px 0 0', lineHeight: 0.9, height: 30, width: 30 }}
        loop={loading}
        width={30}
        height={30}
      />
    )
  }

  useEffect(() => {
    if (!account?.id) return
    checklist.forEach((item) => {
      if (!item.fetch) {
        return
      }

      dispatch(item.fetch())
    })
  }, [account?.id])

  return (
    <div className={className}>
      <Card title='Onboarding checklist'>
        <div className={styles.container}>
          {checklist.map((item, index) => (
            <Link
              to={item.url} key={index} className={
                classnames(!item.loading() && (item.checked() ? styles.success : styles.error))
              }
            >
              {lottie(item.loading(), item.checked())}
              {item.text}
            </Link>
          ))}
        </div>
      </Card>
    </div>
  )
}

export default OnboardingChecklist
